import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeature } from '../contexts/FeatureContext';
import { Video, ArrowRight, Star, Check, User, Shield, MessageSquare, Zap, Award, Clock, QrCode } from 'lucide-react';

const Home = () => {
  const { currentUser } = useAuth();
  const { isFeatureEnabled } = useFeature();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const tools = [
    {
      id: 'youtube-reel',
      name: 'YouTube Reel Creator',
      description: 'Extract segments from YouTube videos and compile them into social media ready reels with customizable aspect ratios.',
      icon: Video,
      popular: true,
      features: [
        'Extract specific video segments',
        'Multiple aspect ratios (9:16, 16:9, 4:5, 1:1)',
        'High-quality output',
        'Easy-to-use interface'
      ]
    },
    {
      id: 'form-connect',
      name: 'FormConnect',
      description: 'Create custom forms that send submissions directly to your WhatsApp. Perfect for businesses and professionals.',
      icon: MessageSquare,
      popular: true,
      features: [
        'Custom form builder',
        'WhatsApp integration',
        'QR code sharing',
        'Multiple field types'
      ]
    },
    {
      id: 'social-media-content',
      name: 'Social Media Content Generator',
      description: 'Create engaging, platform-optimized social media content in seconds using AI. Perfect for marketers and content creators.',
      icon: Star,
      popular: true,
      features: [
        'AI-powered content generation',
        'Platform-specific optimization',
        'Multiple content types',
        'Customizable tone and style'
      ]
    },
    {
      id: 'digital-identity',
      name: 'Digital Identity Hub',
      description: 'Create and share professional digital business cards and personal landing pages with QR codes. Modern networking made easy.',
      icon: QrCode,
      popular: true,
      features: [
        'Professional templates',
        'QR code generation',
        'Contact information sharing',
        'Social media integration'
      ]
    }
  ];

  return (
    <div className="bg-gray-50 overflow-hidden">
      {/* Hero Section with Animation */}
      <div className="relative bg-gradient-to-br from-indigo-800 via-indigo-700 to-indigo-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-indigo-800 opacity-70"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzBoLTZsMy0zIDMgM3ptLTYgMGg2bC0zIDMtMy0zeiIvPjwvZz48L2c+PC9zdmc+')] opacity-10"></div>
        </div>

        <div className="max-w-7xl mx-auto">
          <div className="relative z-10 pb-8 sm:pb-16 md:pb-20 lg:max-w-2xl lg:w-full lg:pb-28 xl:pb-32">
            <svg
              className="hidden lg:block absolute right-0 inset-y-0 h-full w-48 text-indigo-700 transform translate-x-1/2"
              fill="currentColor"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <polygon points="50,0 100,0 50,100 0,100" />
            </svg>

            <div className="pt-10 px-4 sm:pt-12 sm:px-6 md:pt-16 lg:pt-20 lg:px-8 xl:pt-28">
              <div className={`sm:text-center lg:text-left transition-all duration-1000 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                <h1 className="text-4xl tracking-tight font-extrabold text-white sm:text-5xl md:text-6xl">
                  <span className="block relative">
                    Powerful content
                    <span className="absolute -top-5 -right-5 hidden md:block">
                      <Star className="h-8 w-8 text-yellow-300 animate-pulse" />
                    </span>
                  </span>
                  <span className="block text-indigo-300">creation tools</span>
                </h1>
                <p className="mt-3 text-base text-indigo-100 sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-auto lg:text-left">
                  Enhance your workflow with our suite of specialized tools designed for content creators, marketers, and social media professionals.
                </p>
                
                {/* Pricing Badge */}
                <div className="mt-4 inline-flex items-center px-4 py-2 rounded-full bg-green-100 text-green-800 font-bold text-lg border-2 border-green-200 animate-bounce">
                  <span className="mr-2">🎁</span> 100% FREE For Now!
                </div>
                
                <div className="mt-5 sm:mt-8 sm:flex sm:justify-center lg:justify-start">
                  {currentUser ? (
                    <div className="rounded-md shadow">
                      <Link
                        to="/dashboard"
                        className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500 md:py-4 md:text-lg md:px-10 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                      >
                        Go to Dashboard
                      </Link>
                    </div>
                  ) : (
                    <>
                      <div className="rounded-md shadow">
                        <Link
                          to="/signup"
                          className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-500 md:py-4 md:text-lg md:px-10 transition-all duration-300 transform hover:translate-y-[-2px] hover:shadow-lg"
                        >
                          Get Started Free
                        </Link>
                      </div>
                      <div className="mt-3 sm:mt-0 sm:ml-3">
                        <Link
                          to="/signin"
                          className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 md:py-4 md:text-lg md:px-10 transition-all duration-300"
                        >
                          Sign In
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Hero Image/Animation */}
        <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
          <div className="h-56 w-full bg-indigo-900 sm:h-72 md:h-96 lg:w-full lg:h-full">
            <div className="w-full h-full flex items-center justify-center">
              <div className={`relative transition-all duration-1000 transform ${isVisible ? 'scale-100 opacity-100' : 'scale-90 opacity-0'}`}>
                <div className="absolute -inset-4 rounded-full bg-indigo-400 opacity-30 blur-xl animate-pulse"></div>
                <div className="relative">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 transform rotate-3 animate-fadeIn shadow-xl">
                      <Video className="h-12 w-12 text-indigo-300 mb-2" />
                      <div className="w-24 h-2 bg-indigo-300/50 rounded-full mb-2"></div>
                      <div className="w-16 h-2 bg-indigo-300/30 rounded-full"></div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 transform -rotate-3 animate-fadeIn shadow-xl animation-delay-200">
                      <MessageSquare className="h-12 w-12 text-indigo-300 mb-2" />
                      <div className="w-20 h-2 bg-indigo-300/50 rounded-full mb-2"></div>
                      <div className="w-12 h-2 bg-indigo-300/30 rounded-full"></div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 transform rotate-2 animate-fadeIn shadow-xl animation-delay-300">
                      <Star className="h-12 w-12 text-indigo-300 mb-2" />
                      <div className="w-20 h-2 bg-indigo-300/50 rounded-full mb-2"></div>
                      <div className="w-14 h-2 bg-indigo-300/30 rounded-full"></div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 transform -rotate-2 animate-fadeIn shadow-xl animation-delay-400">
                      <QrCode className="h-12 w-12 text-indigo-300 mb-2" />
                      <div className="w-24 h-2 bg-indigo-300/50 rounded-full mb-2"></div>
                      <div className="w-16 h-2 bg-indigo-300/30 rounded-full"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tools Section with Animation */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-indigo-600 font-semibold tracking-wide uppercase inline-flex items-center">
              <Zap className="h-5 w-5 mr-2" />
              Our Tools
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Everything you need to create amazing content
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              Our suite of specialized tools will help you optimize your workflow and create high-quality content faster than ever.
            </p>
          </div>

          <div className="mt-12">
            <div className="space-y-10 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-10">
              {tools.filter(tool => isFeatureEnabled(tool.id)).map((tool, index) => (
                <div 
                  key={tool.id} 
                  className="bg-white overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 rounded-xl border border-gray-100 transform hover:-translate-y-1"
                >
                  <div className="p-6">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-3 shadow-md">
                        <tool.icon className="h-8 w-8 text-white" aria-hidden="true" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-xl leading-6 font-bold text-gray-900 flex items-center">
                          {tool.name}
                          {tool.popular && (
                            <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 animate-pulse">
                              <Star className="h-3 w-3 mr-1" /> Popular
                            </span>
                          )}
                        </h3>
                      </div>
                    </div>

                    <div className="mt-4">
                      <p className="text-base text-gray-500">
                        {tool.description}
                      </p>
                      
                      <div className="mt-5">
                        <h4 className="text-sm font-medium text-gray-900 flex items-center">
                          <Award className="h-4 w-4 mr-1 text-indigo-500" />
                          Features:
                        </h4>
                        <ul className="mt-2 space-y-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {tool.features.map((feature, index) => (
                            <li key={index} className="flex items-start">
                              <Check className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                              <span className="text-sm text-gray-600">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-gray-50 to-indigo-50 px-6 py-4 border-t border-gray-100">
                    {currentUser ? (
                      <Link
                        to={`/${tool.id}`}
                        className="group inline-flex items-center text-base font-medium text-indigo-600 hover:text-indigo-800"
                      >
                        Use this tool
                        <ArrowRight className="ml-1 h-5 w-5 text-indigo-500 group-hover:text-indigo-700 transition-transform group-hover:translate-x-1" />
                      </Link>
                    ) : (
                      <Link
                        to="/signup"
                        className="group inline-flex items-center text-base font-medium text-indigo-600 hover:text-indigo-800"
                      >
                        Sign up to use
                        <ArrowRight className="ml-1 h-5 w-5 text-indigo-500 group-hover:text-indigo-700 transition-transform group-hover:translate-x-1" />
                      </Link>
                    )}
                    
                    {/* Free label */}
                    <span className="float-right inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-green-100 text-green-800">
                      Free
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section with Animations */}
      <div className="bg-gradient-to-b from-gray-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center mb-12">
            <h2 className="text-base text-indigo-600 font-semibold tracking-wide uppercase flex items-center justify-center">
              <Award className="h-5 w-5 mr-2" />
              Why Choose KangleiCode Tools
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Built for creators like you
            </p>
          </div>

          <div className="mt-10">
            <dl className="space-y-10 md:space-y-0 md:grid md:grid-cols-3 md:gap-8">
              <div className="relative bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                <dt>
                  <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                    <Shield className="h-6 w-6" />
                  </div>
                  <p className="ml-16 text-lg leading-6 font-bold text-gray-900">Secure and private</p>
                </dt>
                <dd className="mt-2 ml-16 text-base text-gray-500">
                  Your content and data are secure with us. We never store your videos or content permanently.
                </dd>
              </div>

              <div className="relative bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                <dt>
                  <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                    <User className="h-6 w-6" />
                  </div>
                  <p className="ml-16 text-lg leading-6 font-bold text-gray-900">User-friendly</p>
                </dt>
                <dd className="mt-2 ml-16 text-base text-gray-500">
                  Our tools are designed with simplicity in mind, making them accessible for creators of all skill levels.
                </dd>
              </div>
              
              <div className="relative bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                <dt>
                  <div className="absolute flex items-center justify-center h-12 w-12 rounded-md bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                    <Clock className="h-6 w-6" />
                  </div>
                  <p className="ml-16 text-lg leading-6 font-bold text-gray-900">Time-saving</p>
                </dt>
                <dd className="mt-2 ml-16 text-base text-gray-500">
                  Automate repetitive tasks and focus on what matters most - creating amazing content that stands out.
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div className="py-16 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:text-center">
            <h2 className="text-base text-indigo-600 font-semibold tracking-wide uppercase flex items-center justify-center">
              <Zap className="h-5 w-5 mr-2" />
              Pricing
            </h2>
            <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Simple, transparent pricing
            </p>
          </div>
          
          <div className="mt-12 flex justify-center">
            <div className="relative bg-white rounded-2xl shadow-xl overflow-hidden max-w-lg w-full transform transition-all duration-500 hover:scale-105">
              <div className="absolute top-0 inset-x-0 h-3 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500"></div>
              <div className="px-6 pt-12 pb-8">
                <div className="flex justify-center">
                  <span className="inline-flex px-4 py-1 rounded-full text-xl font-semibold tracking-wide uppercase bg-green-100 text-green-800">
                    Free Access
                  </span>
                </div>
                <div className="mt-8 flex justify-center text-center">
                  <span className="text-5xl font-extrabold text-gray-900">$0</span>
                  <span className="text-xl font-medium text-gray-500 self-end ml-1">/month</span>
                </div>
                <p className="mt-4 text-center text-gray-500">For a limited time, all tools are completely free!</p>
                
                <ul className="mt-8 space-y-4">
                  {[
                    'Unlimited YouTube Reel creations', 
                    'WhatsApp form builder', 
                    'AI social media content generation',
                    'Digital business cards with QR codes',
                    'Custom templates for all tools',
                    'No usage limits'
                  ].map((feature) => (
                    <li key={feature} className="flex items-start">
                      <div className="flex-shrink-0">
                        <Check className="h-6 w-6 text-green-500" />
                      </div>
                      <p className="ml-3 text-base text-gray-700">{feature}</p>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="px-6 pt-6 pb-8 bg-gray-50 border-t border-gray-100">
                {currentUser ? (
                  <Link
                    to="/dashboard"
                    className="block w-full bg-indigo-600 border border-transparent rounded-md shadow py-3 px-6 text-center text-base font-medium text-white hover:bg-indigo-700 transition duration-300"
                  >
                    Go to Dashboard
                  </Link>
                ) : (
                  <Link
                    to="/signup"
                    className="block w-full bg-indigo-600 border border-transparent rounded-md shadow py-3 px-6 text-center text-base font-medium text-white hover:bg-indigo-700 transition duration-300"
                  >
                    Get Started Free
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section with Animation */}
      <div className="bg-gradient-to-r from-indigo-800 to-indigo-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzBoLTZsMy0zIDMgM3ptLTYgMGg2bC0zIDMtMy0zeiIvPjwvZz48L2c+PC9zdmc+')] opacity-10"></div>
        </div>
        
        <div className="max-w-2xl mx-auto text-center py-16 px-4 sm:py-20 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
            <span className="block">Ready to boost your content creation?</span>
          </h2>
          <p className="mt-4 text-lg leading-6 text-indigo-100">
            Join thousands of creators who trust KangleiCode Tools to streamline their workflow.
          </p>
          
          {/* Animated Badge */}
          <div className="mt-6 inline-block">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-green-100 text-green-800 font-bold text-lg border-2 border-green-200 animate-pulse">
              <span className="mr-2">🎁</span> 100% FREE For Now!
            </div>
          </div>
          
          {!currentUser && (
            <div className="mt-8 flex justify-center">
              <div className="inline-flex rounded-md shadow">
                <Link
                  to="/signup"
                  className="inline-flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-md text-indigo-700 bg-white hover:bg-indigo-50 transition-all duration-300 transform hover:scale-105"
                >
                  Get started for free
                  <ArrowRight className="ml-2 -mr-1 h-5 w-5" />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;
